/**
 * 🎵 BEATLEAP WIZARD
 * Multi-Step Flow wie die echte Beatleap App
 * 
 * Step 1: Clips auswählen (Grid)
 * Step 2: Musik auswählen (Kategorien + Liste)
 * Step 3: Processing (Circular Progress)
 * Step 4: Beatleap Studio (Full Editor)
 */
import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronLeft, ChevronRight, Play, Pause, Check } from 'lucide-react';
import { apiClient } from '../../utils/apiClient';
import BeatleapStudio from './BeatleapStudio';

// ====== MUSIK LIBRARY ======
const MUSIC_CATEGORIES = {
  'vertreten': { name: 'Vertreten', emoji: '💪' },
  'reisen': { name: 'Reisen', emoji: '✈️' },
  'partynacht': { name: 'Partynacht', emoji: '🎉' },
  'aesthetic': { name: 'Ästhetik', emoji: '✨' },
};

const MUSIC_TRACKS = [
  { id: 'phonk', name: 'Phonk Drift', artist: 'Suno AI', duration: '03:30', category: 'partynacht', bpm: 136 },
  { id: 'lofi', name: 'Chill Lofi', artist: 'Suno AI', duration: '03:30', category: 'aesthetic', bpm: 85 },
  { id: 'cinematic', name: 'Epic Cinematic', artist: 'Suno AI', duration: '02:50', category: 'vertreten', bpm: 136 },
  { id: 'techno', name: 'Techno Beat', artist: 'Suno AI', duration: '02:50', category: 'partynacht', bpm: 140 },
];

const TRACK_FILES = {
  'phonk': 'phonk_drift.mp3',
  'lofi': 'chill_lofi.mp3',
  'cinematic': 'epic_cinematic.mp3',
  'techno': 'techno_beat.mp3'
};

// ====== MAIN COMPONENT ======
export default function BeatleapWizard({
  isOpen,
  onClose,
  avatarId,
  availableClips = [],
  editingProject = null,
  onComplete,
  inline = false
}) {
  // Wizard State
  const [step, setStep] = useState(1);
  const [selectedClips, setSelectedClips] = useState([]);
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('vertreten');
  const [beatStyle, setBeatStyle] = useState('balanced');
  
  // Processing State
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState('');
  
  // Result State
  const [resultVideo, setResultVideo] = useState(null);
  
  // Audio Preview State
  const [playingTrackId, setPlayingTrackId] = useState(null);
  const audioRef = useRef(null);
  
  // Galerie-Clips State
  const [galleryClips, setGalleryClips] = useState([]);
  const [loadingClips, setLoadingClips] = useState(false);
  
  // Fetch Galerie-Clips wenn Wizard öffnet
  useEffect(() => {
    if (isOpen) {
      // Reset state
      setStep(1);
      setProgress(0);
      setResultVideo(null);
      
      // Handle editing project
      if (editingProject) {
        const config = editingProject.project_config || {};
        if (config.clips?.length > 0) {
          setSelectedClips(config.clips.map((c, i) => ({
            id: `edit-${i}`,
            type: c.type || 'image',
            url: c.url,
            thumbnail: c.thumbnail || c.url,
            videoUrl: c.videoUrl || null,
            duration: c.duration || 3,
            // 🔧 FIX: Transform-Werte übernehmen!
            x: c.x || 0,
            y: c.y || 0,
            scale: c.scale || 1,
            trimStart: c.trimStart || 0,
            trimEnd: c.trimEnd || (c.duration || 3)
          })));
        }
        if (config.music_track_id) {
          const track = MUSIC_TRACKS.find(t => t.id === config.music_track_id);
          if (track) setSelectedTrack(track);
        }
        if (config.style) setBeatStyle(config.style);
        
        if (editingProject.video_url) {
          setResultVideo({
            url: `https://snapshotpro.ai${editingProject.video_url}`,
            thumbnail: editingProject.thumbnail_url ? `https://snapshotpro.ai${editingProject.thumbnail_url}` : null
          });
          setStep(4);
        } else {
          setStep(2);
        }
      } else {
        setSelectedClips([]);
        setSelectedTrack(null);
      }
      
      // Load gallery clips
      setLoadingClips(true);
      setGalleryClips([]);
      
      const imgUrl = avatarId ? `/api/generations/list?avatar_id=${avatarId}` : `/api/generations/list`;
      const vidUrl = avatarId ? `/api/videos/list?avatar_id=${avatarId}` : `/api/videos/list`;
      
      Promise.all([
        apiClient.get(imgUrl).then(r => r.json()),
        apiClient.get(vidUrl).then(r => r.json())
      ])
        .then(([imgData, vidData]) => {
          const clips = [];
          
          if (imgData.generations && Array.isArray(imgData.generations)) {
            imgData.generations.forEach(gen => {
              const imageArray = gen.image_urls || gen.images || [];
              imageArray.forEach((img, idx) => {
                clips.push({
                  id: `img_${gen.id || gen._id}_${idx}`,
                  type: 'image',
                  url: img,
                  thumbnail: img,
                  createdAt: gen.created_at || gen.createdAt
                });
              });
            });
          }
          
          if (vidData.videos && Array.isArray(vidData.videos)) {
            vidData.videos.forEach(vid => {
              if (vid.status === 'completed' && vid.video_url) {
                clips.push({
                  id: `vid_${vid._id || vid.id}`,
                  type: 'video',
                  url: vid.video_url.startsWith('http') ? vid.video_url : `https://snapshotpro.ai${vid.video_url}`,
                  thumbnail: vid.source_image_url || vid.video_url,
                  duration: vid.duration || 5,
                  createdAt: vid.created_at || vid.createdAt
                });
              }
            });
          }
          
          clips.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
          setGalleryClips(clips);
        })
        .catch(err => console.error('Failed to load clips:', err))
        .finally(() => setLoadingClips(false));
    }
  }, [isOpen, avatarId, editingProject]);
  
  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [step]);
  
  // ====== EAGER-LOADING Cache (MUSS vor early return sein!) ======
  const preloadCache = useRef(new Map());
  
  if (!isOpen) return null;
  
  // ====== CLIP SELECTION mit EAGER-LOADING ======
  
  const toggleClipSelection = (clip) => {
    setSelectedClips(prev => {
      const exists = prev.find(c => c.id === clip.id);
      if (exists) return prev.filter(c => c.id !== clip.id);
      
      // ⚡ AGGRESSIVE EAGER-LOADING: Video KOMPLETT im Hintergrund laden!
      if (clip.type === 'video' && !preloadCache.current.has(clip.id)) {
        const video = document.createElement('video');
        video.preload = 'auto';
        video.muted = true;
        video.playsInline = true;
        video.src = clip.url?.startsWith('http') ? clip.url : `https://snapshotpro.ai${clip.url}`;
        
        // Starte Playback um Browser zum Buffern zu zwingen
        video.load();
        video.play().then(() => {
          video.pause();
          video.currentTime = 0;
          console.log(`✅ EAGER-LOAD COMPLETE: Video ${clip.id} ready!`);
        }).catch(() => {
          video.load(); // Fallback
        });
        
        preloadCache.current.set(clip.id, video);
        console.log(`⚡ EAGER-LOAD: Video ${clip.id} wird vorgeladen`);
      }
      
      return [...prev, clip];
    });
  };
  
  const isClipSelected = (clip) => selectedClips.some(c => c.id === clip.id);
  
  // ====== AUDIO PREVIEW ======
  const toggleAudioPreview = (track, e) => {
    e.stopPropagation();
    
    if (playingTrackId === track.id) {
      audioRef.current?.pause();
      audioRef.current = null;
      setPlayingTrackId(null);
    } else {
      if (audioRef.current) audioRef.current.pause();
      
      const filename = TRACK_FILES[track.id] || `${track.id}.mp3`;
      const audio = new Audio(`https://snapshotpro.ai/storage/music/${filename}`);
      audio.volume = 0.7;
      audio.load();
      audio.play().then(() => setPlayingTrackId(track.id)).catch(() => {});
      audio.onended = () => setPlayingTrackId(null);
      audioRef.current = audio;
    }
  };
  
  // ====== PROCESS VIDEO ======
  const processVideo = async () => {
    setStep(3);
    setProcessing(true);
    setProgress(0);
    setProgressText('Video erstellen...');
    
    try {
      const response = await apiClient.post('/api/beatleap/create', {
        assets: selectedClips.map(c => ({
          type: c.type,
          url: c.url,
          thumbnail: c.thumbnail
        })),
        music_track: selectedTrack?.id || 'lofi',
        style: beatStyle
      });
      
      if (!response.ok) throw new Error('API Fehler');
      
      const data = await response.json();
      if (!data.success || !data.job_id) {
        throw new Error(data.error || 'Job konnte nicht gestartet werden');
      }
      
      // Poll job status
      let pollAttempts = 0;
      const maxPollAttempts = 600;
      let consecutiveErrors = 0;
      
      const pollJob = async () => {
        pollAttempts++;
        
        if (pollAttempts > maxPollAttempts) {
          setProcessing(false);
          alert('Video-Erstellung dauert zu lange.');
          setStep(2);
          return;
        }
        
        try {
          const statusRes = await apiClient.get(`/api/beatleap/job/${data.job_id}`);
          
          if (!statusRes.ok) {
            consecutiveErrors++;
            if (consecutiveErrors < 5) {
              setTimeout(pollJob, 2000);
              return;
            }
            throw new Error('Status-Abruf fehlgeschlagen');
          }
          
          consecutiveErrors = 0;
          const status = await statusRes.json();
          
          setProgress(status.progress);
          setProgressText(status.message);
          
          if (status.status === 'completed') {
            const video = {
              url: `https://snapshotpro.ai${status.video_url}`,
              thumbnail: status.thumbnail_url ? `https://snapshotpro.ai${status.thumbnail_url}` : null
            };
            setResultVideo(video);
            setStep(4);
            setProcessing(false);
            onComplete?.(video);
          } else if (status.status === 'failed') {
            throw new Error(status.message || 'Rendering fehlgeschlagen');
          } else {
            setTimeout(pollJob, pollAttempts > 60 ? 2000 : 1000);
          }
        } catch (err) {
          consecutiveErrors++;
          if (consecutiveErrors < 5) {
            setTimeout(pollJob, 3000);
            return;
          }
          setProcessing(false);
          alert('Fehler: ' + err.message);
          setStep(2);
        }
      };
      
      pollJob();
      
    } catch (error) {
      console.error('Process error:', error);
      setProcessing(false);
      alert('Fehler: ' + error.message);
      setStep(2);
    }
  };
  
  // ====== RENDER ======
  return (
    <div className={inline ? "flex flex-col bg-black/90 rounded-xl min-h-[60vh] max-h-[80vh]" : "fixed inset-0 z-[9999] bg-black flex flex-col"}>
      
      {/* === HEADER (Steps 1-3) === */}
      {step < 4 && (
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          {step > 1 ? (
            <button onClick={() => setStep(step - 1)} className="flex items-center gap-1 text-white/70">
              <ChevronLeft className="w-5 h-5" />
              <span>Zurück</span>
            </button>
          ) : (
            <button onClick={onClose} className="p-2"><X className="w-6 h-6 text-white/70" /></button>
          )}
          
          <h1 className="text-[#10B981] font-bold text-lg">
            {step === 1 && 'Clips wählen'}
            {step === 2 && 'Musik wählen'}
            {step === 3 && 'Rendering...'}
          </h1>
          
          {step === 1 && selectedClips.length >= 1 && (
            <button onClick={() => setStep(4)} className="flex items-center gap-1 bg-[#10B981] text-black px-4 py-2 rounded-full font-medium">
              <span>Zur Werkstatt</span><ChevronRight className="w-5 h-5" />
            </button>
          )}
          {step === 2 && selectedTrack && (
            <button onClick={processVideo} className="flex items-center gap-1 bg-[#10B981] text-black px-4 py-2 rounded-full font-medium">
              Video erstellen
            </button>
          )}
          {((step === 1 && selectedClips.length < 1) || (step === 2 && !selectedTrack) || step === 3) && <div className="w-16" />}
        </div>
      )}
      
      {/* === STEP 1: CLIP SELECTION === */}
      {step === 1 && (
        <div className="flex-1 overflow-y-auto">
          <div className="flex border-b border-white/10 sticky top-0 bg-black z-10">
            <button className="flex-1 py-3 text-white border-b-2 border-[#10B981] font-medium">ALLE</button>
          </div>
          
          {selectedClips.length < 1 && !loadingClips && (
            <div className="px-4 py-3 bg-[#10B981]/10 text-[#10B981] text-sm text-center">
              Wähle mindestens 1 Clip
            </div>
          )}
          
          {loadingClips && (
            <div className="flex-1 flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-[#10B981]/30 border-t-[#10B981] rounded-full animate-spin" />
            </div>
          )}
          
          {!loadingClips && (
            <div className="grid grid-cols-4 gap-0.5 p-0.5">
              {galleryClips.map((clip, index) => (
                <button key={clip.id || index} onClick={() => toggleClipSelection(clip)} className="relative aspect-square">
                  <img src={clip.thumbnail || clip.url} alt="" className="w-full h-full object-cover" />
                  {clip.type === 'video' && (
                    <div className="absolute top-1 left-1 bg-blue-500/80 text-white p-1 rounded">
                      <Play className="w-3 h-3" />
                    </div>
                  )}
                  {isClipSelected(clip) && (
                    <>
                      <div className="absolute inset-0 bg-[#10B981]/30 flex items-center justify-center">
                        <div className="w-8 h-8 bg-[#10B981] rounded-full flex items-center justify-center">
                          <Check className="w-5 h-5 text-black" />
                        </div>
                      </div>
                      <div className="absolute top-1 right-1 w-6 h-6 bg-[#10B981] rounded-full flex items-center justify-center text-black text-xs font-bold">
                        {selectedClips.findIndex(c => c.id === clip.id) + 1}
                      </div>
                    </>
                  )}
                </button>
              ))}
            </div>
          )}
          
          {galleryClips.length === 0 && !loadingClips && (
            <div className="flex-1 flex items-center justify-center text-white/40 py-20">
              Keine Clips verfügbar. Generiere erst Bilder oder Videos!
            </div>
          )}
        </div>
      )}
      
      {/* === STEP 2: MUSIC SELECTION === */}
      {step === 2 && (
        <div className="flex-1 overflow-y-auto">
          <div className="flex border-b border-white/10 sticky top-0 bg-black z-10 overflow-x-auto">
            {Object.entries(MUSIC_CATEGORIES).map(([key, cat]) => (
              <button
                key={key}
                onClick={() => setSelectedCategory(key)}
                className={`px-4 py-3 whitespace-nowrap font-medium transition-colors ${
                  selectedCategory === key ? 'text-white border-b-2 border-[#10B981]' : 'text-white/50'
                }`}
              >
                {cat.name.toUpperCase()}
              </button>
            ))}
          </div>
          
          <div className="px-4 py-3 border-b border-white/10">
            <p className="text-white/50 text-xs mb-2">Schnitt-Style</p>
            <div className="flex gap-2">
              {[
                { id: 'fast', name: 'Schnell' },
                { id: 'balanced', name: 'Ausgewogen' },
                { id: 'cinematic', name: 'Cinematic' },
              ].map(style => (
                <button
                  key={style.id}
                  onClick={() => setBeatStyle(style.id)}
                  className={`flex-1 py-2 px-2 rounded-lg text-center text-xs transition-all ${
                    beatStyle === style.id ? 'bg-[#10B981] text-black' : 'bg-white/10 text-white/70'
                  }`}
                >
                  {style.name}
                </button>
              ))}
            </div>
          </div>
          
          <div className="divide-y divide-white/5">
            {MUSIC_TRACKS.filter(t => t.category === selectedCategory).map(track => (
              <div
                key={track.id}
                onClick={() => setSelectedTrack(track)}
                className={`w-full flex items-center gap-3 p-4 text-left transition-colors cursor-pointer ${
                  selectedTrack?.id === track.id ? 'bg-[#10B981]/20' : 'hover:bg-white/5'
                }`}
              >
                <button
                  onClick={(e) => toggleAudioPreview(track, e)}
                  className={`w-14 h-14 rounded-lg flex items-center justify-center transition-all ${
                    playingTrackId === track.id 
                      ? 'bg-[#10B981] animate-pulse' 
                      : selectedTrack?.id === track.id 
                        ? 'bg-[#10B981]' 
                        : 'bg-gradient-to-br from-purple-500 to-pink-500'
                  }`}
                >
                  {playingTrackId === track.id ? (
                    <Pause className="w-6 h-6 text-black" />
                  ) : (
                    <Play className={`w-6 h-6 ${selectedTrack?.id === track.id ? 'text-black' : 'text-white'} ml-0.5`} />
                  )}
                </button>
                
                <div className="flex-1 min-w-0">
                  <div className={`font-medium truncate ${selectedTrack?.id === track.id ? 'text-[#10B981]' : 'text-white'}`}>
                    {track.name}
                  </div>
                  <div className="text-white/50 text-sm">{track.artist} · {track.duration}</div>
                </div>
                
                <div className="text-white/30 text-xs">{track.bpm} BPM</div>
                
                {selectedTrack?.id === track.id && (
                  <div className="w-6 h-6 bg-[#10B981] rounded-full flex items-center justify-center">
                    <Check className="w-4 h-4 text-black" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* === STEP 3: PROCESSING === */}
      {step === 3 && !resultVideo && (
        <div className="flex-1 flex flex-col items-center justify-center px-8">
          <div className="relative w-32 h-32 mb-6">
            <svg className="w-full h-full animate-spin" style={{ animationDuration: '3s' }}>
              <circle cx="64" cy="64" r="58" fill="none" stroke="url(#gradient)" strokeWidth="6" strokeLinecap="round" strokeDasharray="100 265" />
              <defs>
                <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#10B981" />
                  <stop offset="100%" stopColor="transparent" />
                </linearGradient>
              </defs>
            </svg>
            <svg className="w-full h-full absolute inset-0 transform -rotate-90">
              <circle cx="64" cy="64" r="50" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="4" />
              <circle cx="64" cy="64" r="50" fill="none" stroke="#10B981" strokeWidth="4" strokeLinecap="round"
                strokeDasharray={`${2 * Math.PI * 50}`}
                strokeDashoffset={`${2 * Math.PI * 50 * (1 - progress / 100)}`}
                className="transition-all duration-500"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-white text-2xl font-bold">{Math.round(progress)}%</span>
            </div>
          </div>
          <p className="text-white text-lg mb-2 text-center">{progressText || 'Verarbeite...'}</p>
          <p className="text-white/40 text-sm">Medien zum Beat einstellen</p>
          <button onClick={() => { setProcessing(false); setStep(2); }} className="mt-8 text-white/50 hover:text-white">
            Abbrechen
          </button>
        </div>
      )}
      
      {/* === STEP 4: BEATLEAP STUDIO (WERKSTATT) === */}
      {step === 4 && console.log('🎯 WIZARD → STUDIO:', selectedClips.length, 'Clips übergeben') && false}
      {step === 4 && (
        <BeatleapStudio
          isOpen={true}
          resultVideo={resultVideo}
          selectedTrack={selectedTrack}
          galleryClips={galleryClips}
          initialClips={selectedClips} // NEU: Clips direkt ans Studio übergeben
          avatarId={avatarId} // FIX: avatarId weitergeben für Magic
          onClose={onClose}
          onNewProject={() => {
            setStep(1);
            setResultVideo(null);
            setSelectedClips([]);
          }}
        />
      )}
      
      {/* === BOTTOM INFO (Step 1 & 2) === */}
      {(step === 1 || step === 2) && (
        <div className="px-4 py-3 bg-black/50 border-t border-white/10">
          <div className="flex items-center justify-center gap-2 text-white/50 text-sm">
            <span className="text-[#10B981] font-bold">{selectedClips.length}</span>
            <span>Clips ausgewählt</span>
            {selectedTrack && (
              <>
                <span>·</span>
                <span className="text-[#10B981]">{selectedTrack.name}</span>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
